/**
 * Zdas (GeOZ-ZDAS acquisition) flight log `FLT<nnn>.LOG` parsing: aircraft rego, every line ended with its
 * flown length, and the test / comp-box / ground-check lines flown. Format from PROJECT_FOLDER_STRUCTURE.md
 * and report/make_weekly.py (Flight).
 */
import { parseLineNumber, zdasToLineNumber } from './lines.js';
import type { OnlineRun } from './airborne-split.js';
import type { CalKind } from './model.js';

export interface ZdasFlight {
  readonly rego: string | null;
  /** Flown km per production line number (version digit included); a line ended twice accumulates. */
  readonly lines: Readonly<Record<string, number>>;
  /**
   * The completion marker Zdas wrote when each production line ended, as logged without the brackets: `Comp 100%`
   * (fully flown), `Part 99.7%` (partly flown), `na`. A line ended more than once keeps `Comp 100%` if any end has
   * it, else its last marker. A line whose `Ended` row has no marker has no entry. Decision 176: this, not a
   * percentage of the planned length, says whether a line was fully flown (`isFullyFlown`).
   */
  readonly completion?: Readonly<Record<string, string>>;
  /** Km flown on non-production lines (test, comp box, ground check). */
  readonly testKm: number;
  readonly compBox: boolean;
  /** Comp-box line numbers flown (7010, 7020, 7011 …). */
  readonly compLines: readonly number[];
  /** Heading in degrees logged when each production line was started (`Started … Dir:178`); the first start wins. */
  readonly directions?: Readonly<Record<string, number>>;
  /**
   * Easting and northing logged where each production line started (its first `Started`) and ended (its last
   * `Ended`), in the log's grid. A line without both has no entry.
   */
  readonly extents?: Readonly<Record<string, LineExtent>>;
  /** The log's Zdas sessions, in log order (Rust parser `teslyn_core::zdas_log`; not filled by `parseZdasLog`). */
  readonly sessions?: readonly ZdasSession[];
  /** Ground cals, thorium cal results and test lines, in log order (Rust parser; not filled by `parseZdasLog`). */
  readonly cals?: readonly ZdasCalEvent[];
  /** Minutes online per production line (Rust parser, MB's R05; not filled by `parseZdasLog`). */
  readonly online?: Readonly<Record<string, number>>;
  /** Runs of test lines 801 and 802 and the test line Zdas captured (Rust parser, R14). */
  readonly testRuns?: readonly TestLineRun[];
  /** Every `Ended` row's online time and end, in log order (Rust parser, R47: the airborne time split). */
  readonly onlineRuns?: readonly OnlineRun[];
}

/**
 * A run of test line 801 or 802, or the test line Zdas captured into its job file ("New test line captured and saved
 * to JOB file with length = 10.007 km" and its row of line, zone, start and end). `[easting, northing]`.
 */
export interface TestLineRun {
  readonly line: number;
  readonly number: number;
  readonly start: readonly [number, number];
  readonly end: readonly [number, number];
  readonly km: number;
  readonly startedLocal: string | null;
  readonly captured: boolean;
  readonly lineNo: number;
}

/** One Zdas session (one start of the acquisition program) of a log. Local times are `YYYY-MM-DDTHH:MM:SS`. */
export interface ZdasSession {
  readonly index: number;
  readonly startLine: number;
  readonly localJulian: number | null;
  readonly startedLocal: string | null;
  readonly localTimeOffsetH: number | null;
  readonly flt: number | null;
  readonly rego: string | null;
  readonly jobFile: string | null;
  readonly linesFile: string | null;
  readonly takeoffLocal: string | null;
  readonly landingLocal: string | null;
  /** Production lines (full numbers) ended in the session. */
  readonly productionLines: readonly number[];
}

/** Zdas's comparison of a background (Th − Bk) or a test line with the job-file average typed into Zdas. */
export interface ZdasComparison {
  readonly value: number;
  readonly reference: number;
  readonly pct: number;
  readonly pass: boolean;
}

/** A calibration event of a log (inventory 02 §5.2). */
export interface ZdasCalEvent {
  readonly kind: CalKind;
  /** 502/501 pre-flight sample/background, 602/601 post-flight, 801/802 test lines; a Th cal result its sample's. */
  readonly groundLine: number | null;
  /** Index of the session (`ZdasFlight.sessions`). */
  readonly session: number;
  /** 1-based line of the event in the log. */
  readonly lineNo: number;
  /** Local date and time recorded (the line's end, else "recorded at" + `LocalTimeOffset`). */
  readonly recordedLocal: string | null;
  /** "recorded at" as printed (UTC for ground cals). */
  readonly recordedAt: string | null;
  readonly samples: number | null;
  readonly tc: number | null;
  readonly k: number | null;
  readonly u: number | null;
  readonly th: number | null;
  readonly cosmic: number | null;
  readonly easting: number | null;
  readonly northing: number | null;
  readonly altitudeM: number | null;
  readonly thPeak: number | null;
  readonly fwhmPct: number | null;
  readonly zdas: ZdasComparison | null;
}

/** The logged start and end points of a line, `[easting, northing]`. */
export interface LineExtent {
  readonly start: readonly [number, number];
  readonly end: readonly [number, number];
}

const REGO = /AircraftRego=(\S+)/;
/** `… Len =  12822 m (12.822 km) Online time 2.9 min [Comp 100%]`: the marker is the bracketed text, if any. */
const LINE_ENDED =
  /Line\s+(\d+)\.(\d)\s+Ended\s+(\d+)\s+(\d+)\s+Len =\s+(\d+) m[^[\r\n]*(?:\[([^\]\r\n]*)\])?/g;
const FULLY_FLOWN = /^comp\s+100(\.0+)?\s*%$/i;

/**
 * Whether a Zdas completion marker says the line was flown in full (decision 176, Q154): only `Comp 100%`. `Part …`
 * is partly flown; anything else (`na`, no marker, `Comp 87%`) is not fully flown.
 */
export function isFullyFlown(marker: string | null | undefined): boolean {
  return marker != null && FULLY_FLOWN.test(marker.trim());
}

const LINE_STARTED = /Line\s+(\d+)\.(\d)\s+Started\s+(\d+)\s+(\d+)\s+Dir:\s*(\d+)/g;

export function parseZdasLog(text: string): ZdasFlight {
  const rego = REGO.exec(text)?.[1] ?? null;
  const lines: Record<string, number> = {};
  const completion: Record<string, string> = {};
  const ends: Record<string, [number, number]> = {};
  const compLines = new Set<number>();
  let testKm = 0;
  for (const m of text.matchAll(LINE_ENDED)) {
    const n = zdasToLineNumber(m[1]!, m[2]!);
    const km = Number(m[5]) / 1000;
    const parsed = parseLineNumber(n);
    if (!parsed.production) {
      testKm += km;
      if (parsed.kind === 'comp_box') compLines.add(n);
      continue;
    }
    lines[String(n)] = (lines[String(n)] ?? 0) + km;
    ends[String(n)] = [Number(m[3]), Number(m[4])];
    const marker = m[6]?.trim();
    if (marker && !isFullyFlown(completion[String(n)])) completion[String(n)] = marker;
  }
  const directions: Record<string, number> = {};
  const extents: Record<string, LineExtent> = {};
  for (const m of text.matchAll(LINE_STARTED)) {
    const n = zdasToLineNumber(m[1]!, m[2]!);
    if (!parseLineNumber(n).production || String(n) in directions) continue;
    directions[String(n)] = Number(m[5]) % 360;
    const end = ends[String(n)];
    if (end) extents[String(n)] = { start: [Number(m[3]), Number(m[4])], end };
  }
  return {
    rego,
    lines,
    completion,
    testKm,
    compBox: compLines.size > 0,
    compLines: [...compLines].sort((a, b) => a - b),
    directions,
    extents,
  };
}

/**
 * Merge the same flight filed in two block folders (flights touching two blocks are copied into both):
 * union of lines, keeping the larger length where both have the line (and that copy's completion marker; a tie keeps
 * the first flight's).
 */
export function mergeZdasFlights(a: ZdasFlight, b: ZdasFlight): ZdasFlight {
  const lines: Record<string, number> = { ...a.lines };
  const completion: Record<string, string> = { ...b.completion, ...a.completion };
  for (const [k, km] of Object.entries(b.lines)) {
    const marker = b.completion?.[k];
    if (km > (lines[k] ?? 0) && marker !== undefined) completion[k] = marker;
    lines[k] = Math.max(lines[k] ?? 0, km);
  }
  const compLines = [...new Set([...a.compLines, ...b.compLines])].sort((x, y) => x - y);
  return {
    rego: a.rego ?? b.rego,
    lines,
    completion,
    testKm: Math.max(a.testKm, b.testKm),
    compBox: a.compBox || b.compBox,
    compLines,
    directions: { ...b.directions, ...a.directions },
    extents: { ...b.extents, ...a.extents },
  };
}
